import { c as _c } from "react/compiler-runtime";
import type { JSX } from 'react';
import Script from 'next/script';

/** Fired by Cookiebot each time it builds the dialog, including on renew. */
export const COOKIEBOT_DIALOG_DISPLAY_EVENT = 'CookiebotOnDialogDisplay';

/**
 * The three bits of the Cookiebot dialog's Extern skin a stylesheet cannot
 * reach: "Show details" moved into the button group, the title's last word set
 * in serif, and the accept button relabelled. Copied from extern-website's
 * `components/consent/cookiebot-skin-script.tsx`, itself a copy of the
 * `<script>` www.extern.com's Webflow body carries, so every extern.com surface
 * shows the same banner. Edit all or none. The stylesheet half lives in
 * `app/globals.css`.
 *
 * `data-cookieconsent="ignore"`: this draws the consent banner itself, so it
 * has to run when no consent exists yet.
 *
 * Hooks `CookiebotOnDialogDisplay` and not `DOMContentLoaded`: Cookiebot defers
 * `DOMContentLoaded` listeners by several seconds. `CookiebotOnDialogInit` is
 * no alternative either — it fires before the template is built, so the title
 * does not exist yet. Everything here is idempotent and re-runnable, because
 * Cookiebot rebuilds the dialog from scratch on renew and on closing the
 * Customize panel, and the event fires again.
 *
 * `afterInteractive` rather than `beforeInteractive`: `uc.js` is
 * `beforeInteractive` and hydration waits on it, so by the time this runs the
 * dialog may already be on the page — which is why the skin is applied once on
 * parse as well as on every display event.
 */
export function CookiebotSkinScript() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Script data-cookieconsent="ignore" id="extern-cookiebot-skin" strategy="afterInteractive">{`(function () {
  var SERIF_CLASS = 'cb-serif';

  // Cookiebot draws the accept button from multiAcceptText, which ships as "OK".
  // Also settable in the dashboard; done here so it travels with this block.
  //
  // The other footer button is deliberately NOT touched. In this response mode it
  // renders doNotSellOrShareText, and "Do not sell or share my personal information"
  // is the link title Cal. Civ. Code 1798.135(a)(1) prescribes — and it is the only
  // place that string appears on the site, so renaming it would remove it entirely.
  var ACCEPT_LABEL = 'Allow all';

  // The title's last word, set in serif italic. Cookiebot escapes markup in the title
  // field, so this is the only place it can happen.
  function emphasizeLastWord() {
    var title = document.getElementById('CybotCookiebotDialogBodyContentTitle');

    if (!title || title.querySelector('.' + SERIF_CLASS)) { return; }

    var words = title.textContent.trim().split(/\\s+/);

    if (words.length < 2) { return; }

    var lastWord = words.pop();
    var serif = document.createElement('span');

    serif.className = SERIF_CLASS;
    serif.textContent = lastWord;

    title.textContent = words.join(' ') + ' ';
    title.appendChild(serif);
  }

  function relabelAcceptButton() {
    var accept = document.getElementById('CybotCookiebotDialogBodyButtonAccept');

    if (accept && accept.textContent.trim() !== ACCEPT_LABEL) {
      accept.textContent = ACCEPT_LABEL;
    }
  }

  // "Show details" renders inside #...TabContent while the buttons render inside
  // #...Footer -- sibling subtrees under .CybotCookiebotDialogContentWrapper, so flex
  // \`order\` cannot reach across them, and display:contents on the wrappers in between
  // would take the banner's scroll container and the Customize panel down with it.
  // Moving the node drags the tab order along with the visual order; a CSS reorder
  // would have left the two out of step.
  //
  // It goes to #...BodyButtons at every width and stays there -- no restore, so nothing
  // to get stranded. Deliberately not #...BodyButtonsWrapper inside it: that wrapper's
  // children belong to Cookiebot's sortBannerButtons(), which clears it before
  // re-appending the buttons, and a link parked in there is destroyed on the next resize.
  function placeMoreDetails() {
    var moreDetails = document.getElementById('CybotCookiebotDialogBodyEdgeMoreDetails');
    var buttonGroup = document.getElementById('CybotCookiebotDialogBodyButtons');

    if (!moreDetails || !buttonGroup) { return; }
    if (moreDetails.parentElement === buttonGroup) { return; }

    // Appended last, so it lands under the buttons in the desktop column and, once the
    // 1280px block turns the group into a row, wherever \`order\` puts it.
    buttonGroup.appendChild(moreDetails);
  }

  function applyExternSkin() {
    // Each in its own try: placement runs first and independently, because a throw in
    // either of the others used to skip it and leave the link in the wrong subtree.
    try { placeMoreDetails(); } catch (e) { /* never break the page */ }
    try { emphasizeLastWord(); } catch (e) { /* never break the page */ }
    try { relabelAcceptButton(); } catch (e) { /* never break the page */ }
  }

  // Switching tabs does not reset any of these: [#ACCEPT#] is interpolated once
  // when the template is built, and sortBannerButtons() re-appends the same nodes
  // rather than re-rendering them. Only a full rebuild loses them, and that fires
  // CookiebotOnDialogDisplay again.
  window.addEventListener('${COOKIEBOT_DIALOG_DISPLAY_EVENT}', applyExternSkin);
  // In case the dialog is already on the page by the time this parses.
  applyExternSkin();
})();`}</Script>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}